import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import FilmGrain from "@/components/FilmGrain";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";

export const PasswordGate = () => {
  const { t } = useLang();
  const { login } = useAuth();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setBusy(true);
    setErr("");
    try {
      await login(pw);
    } catch (_e) {
      setErr(t("gate.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="password-gate">
      <FilmGrain />
      <div className="flex items-center justify-between px-5 sm:px-8 py-6">
        <span className="font-serif-display text-lg tracking-[0.04em]">Eli &amp; Mag</span>
        <LanguageSwitcher />
      </div>

      <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          className="w-full max-w-[460px]"
        >
          <p className="editorial-kicker">{t("gate.overline")}</p>
          <h1 className="editorial-h1 mt-4 text-[40px] sm:text-[52px] leading-[1.02]">
            {t("gate.title")}
          </h1>
          <p className="mt-6 text-foreground/70 leading-relaxed max-w-[44ch]">
            {t("gate.body")}
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-4">
            <div className="surface-card p-1.5 relative">
              <Input
                type={showPassword ? "text" : "password"}
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder={t("gate.placeholder")}
                aria-label={t("gate.placeholder")}
                data-testid="password-gate-input"
                className="h-12 pr-12 text-base bg-transparent border-0 focus-visible:ring-0 tracking-[0.18em] placeholder:uppercase placeholder:text-foreground/30"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                data-testid="password-gate-toggle-visibility"
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-9 w-9 rounded-md text-foreground/45 hover:text-foreground hover:bg-foreground/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {err && (
              <p
                role="alert"
                data-testid="password-gate-error-text"
                className="text-sm text-destructive"
              >
                {err}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="password-gate-submit-button"
              className="h-12 w-full rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-[0.22em] uppercase text-[12px]"
            >
              {busy ? "…" : t("gate.submit")}
            </Button>
            <p className="editorial-kicker pt-2">{t("gate.forgot")}</p>
          </form>
        </motion.div>
      </div>

      <div className="px-5 sm:px-8 py-6 editorial-kicker text-center">
        19.05.2027 · Ammos Kalambouri · Cyprus
      </div>
    </div>
  );
};

export default PasswordGate;
